/**
 * Every procedure as TanStack Query options. SSR calls go in-process through a `createServerFn`
 * boundary; the client batches over HTTP.
 */
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import { QueryClient } from '@tanstack/react-query';
import type { TRPCLink } from '@trpc/client';
import type { AppRouter } from './trpc/router.server';
import { trpcSSRCallFn } from './trpc-server-fn';
import { telemetryHeaders } from './telemetry-headers';
import { syncVersionFromHeader } from './app-version';
import { MAX_BATCH_URL_LENGTH } from './trpc-batch';

// MARK: SSR link (in-process)

const isServer = typeof window === 'undefined';

/** The server-function boundary is what tree-shakes the `.server.ts` imports out of the client bundle. */
const ssrLink: TRPCLink<AppRouter> = () => {
	return ({ op }) => {
		return observable((observer) => {
			const type = op.type === 'subscription' ? 'query' : op.type;
			trpcSSRCallFn({ data: { path: op.path, input: op.input, type } })
				.then((result) => {
					observer.next({ result: { type: 'data', data: result } });
					observer.complete();
				})
				.catch((err: unknown) => {
					observer.error(
						err instanceof TRPCClientError
							? err
							: new TRPCClientError<AppRouter>(err instanceof Error ? err.message : String(err), {
									cause: err instanceof Error ? err : undefined,
								}),
					);
				});
		});
	};
};

// MARK: tRPC client

export const trpcClient = createTRPCClient<AppRouter>({
	links: isServer
		? [ssrLink]
		: // `headers` per request: viewport and trace id change. `fetch` reads the deployed version back, the
			// only half a page that just polls (the live counter, open on a screen for weeks) ever exercises.
			[
				httpBatchLink({
					url: '/api/trpc',
					// A restored cache once refetched ~110 live-counter windows in a 16 KB URL the service 500ed (TEC-3807/TEC-3810).
					maxURLLength: MAX_BATCH_URL_LENGTH,
					headers: () => telemetryHeaders(),
					fetch: (input, init) =>
						globalThis.fetch(input, init).then((response) => {
							syncVersionFromHeader(response);
							return response;
						}),
				}),
			],
});

// MARK: Options proxy (singleton)

/** Only for tRPC's async-iterable support, which we don't use; the real QueryClient comes from context. */
const placeholderQueryClient = new QueryClient();

/** Needs no React context. */
export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient: placeholderQueryClient,
});
